/**
 * Card - IMPROVEMENT 20
 * Consistent card taxonomy across the application.
 */
import { ReactNode, forwardRef } from 'react'
import { ChevronRight } from 'lucide-react'
import clsx from 'clsx'
import './Card.css'

// Accent colors for accent variant
type AccentColor = 'brand' | 'positive' | 'negative' | 'warning' | 'info'

// Base Card Component
interface CardProps {
  children: ReactNode
  variant?: 'default' | 'surface' | 'elevated' | 'accent' | 'outlined' | 'ghost'
  accentColor?: AccentColor
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'compact' | 'comfortable'
  hover?: boolean
  hoverable?: boolean
  interactive?: boolean
  selected?: boolean
  onClick?: () => void
  className?: string
}

export const Card = forwardRef<HTMLDivElement, CardProps>(({
  children,
  variant = 'default',
  accentColor,
  padding = 'md',
  hover = false,
  hoverable = false,
  interactive = false,
  selected = false,
  onClick,
  className
}, ref) => {
  return (
    <div
      ref={ref}
      className={clsx(
        'card',
        `card--${variant}`,
        variant === 'accent' && accentColor && `card--accent-${accentColor}`,
        `card-padding-${padding}`,
        (hover || hoverable) && 'card-hover',
        interactive && 'card--interactive',
        selected && 'card--selected',
        onClick && 'card-clickable',
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </div>
  )
})

Card.displayName = 'Card'

// Card Header
interface CardHeaderProps {
  children?: ReactNode
  title?: string
  subtitle?: string
  icon?: ReactNode
  action?: ReactNode
  className?: string
}

export function CardHeader({
  children,
  title,
  subtitle,
  icon,
  action,
  className
}: CardHeaderProps) {
  return (
    <div className={clsx('card-header', className)}>
      <div className="card-header-content">
        {icon && <div className="card-header-icon">{icon}</div>}
        {(title || subtitle) ? (
          <div className="card-header-text">
            {title && <h3 className="card-title">{title}</h3>}
            {subtitle && <p className="card-subtitle">{subtitle}</p>}
          </div>
        ) : children}
      </div>
      {action && <div className="card-header-action">{action}</div>}
    </div>
  )
}

// Card Title
interface CardTitleProps {
  children: ReactNode
  className?: string
}

export function CardTitle({ children, className }: CardTitleProps) {
  return <h3 className={clsx('card-title', className)}>{children}</h3>
}

// Card Body/Content
interface CardContentProps {
  children: ReactNode
  className?: string
}

export function CardContent({ children, className }: CardContentProps) {
  return <div className={clsx('card-content', className)}>{children}</div>
}

// Alias for CardContent
export const CardBody = CardContent

// Card Footer
interface CardFooterProps {
  children: ReactNode
  align?: 'left' | 'center' | 'right' | 'between'
  className?: string
}

export function CardFooter({ children, align = 'right', className }: CardFooterProps) {
  return (
    <div className={clsx('card-footer', align && `card-footer--${align}`, className)}>
      {children}
    </div>
  )
}

// Stat Card
interface StatCardProps {
  label: string
  value: string | number
  change?: number
  trend?: 'up' | 'down' | 'neutral'
  icon?: ReactNode
  prefix?: string
  suffix?: string
  size?: 'sm' | 'md' | 'lg'
  onClick?: () => void
  className?: string
}

export function StatCard({
  label,
  value,
  change,
  trend,
  icon,
  prefix,
  suffix,
  size = 'md',
  onClick,
  className
}: StatCardProps) {
  return (
    <Card
      variant="default"
      padding={size === 'sm' ? 'sm' : 'md'}
      hover={!!onClick}
      onClick={onClick}
      className={clsx('stat-card', `stat-card--${size}`, className)}
    >
      <div className="stat-card-header">
        <span className="stat-card-label">{label}</span>
        {icon && <div className="stat-card-icon">{icon}</div>}
      </div>
      <div className="stat-card-value">
        {prefix && <span className="stat-card-prefix">{prefix}</span>}
        {value}
        {suffix && <span className="stat-card-suffix">{suffix}</span>}
      </div>
      {change !== undefined && (
        <div className={clsx(
          'stat-card-change',
          trend === 'up' && 'positive',
          trend === 'down' && 'negative'
        )}>
          {change >= 0 ? '+' : ''}{change.toFixed(2)}%
        </div>
      )}
    </Card>
  )
}

// Stock Card
interface StockCardProps {
  ticker: string
  name?: string
  price: number
  change: number
  changePercent: number
  volume?: number
  marketCap?: string
  onClick?: () => void
  actions?: ReactNode
  showArrow?: boolean
  compact?: boolean
  className?: string
}

export function StockCard({
  ticker,
  name,
  price,
  change,
  changePercent,
  volume,
  marketCap,
  onClick,
  actions,
  showArrow = false,
  compact = false,
  className
}: StockCardProps) {
  const isPositive = change >= 0

  return (
    <Card
      variant="default"
      padding={compact ? 'sm' : 'md'}
      hover={!!onClick}
      interactive={!!onClick}
      onClick={onClick}
      className={clsx('stock-card', compact && 'stock-card--compact', className)}
    >
      <div className="stock-card-header">
        <div className="stock-card-ticker-group">
          <span className="stock-card-ticker">{ticker}</span>
          {name && <span className="stock-card-name">{name}</span>}
        </div>
        {actions ? (
          <div className="stock-card-actions">{actions}</div>
        ) : showArrow ? (
          <ChevronRight size={16} className="stock-card-arrow" />
        ) : null}
      </div>

      <div className="stock-card-price-group">
        <span className="stock-card-price">${price.toFixed(2)}</span>
        <span className={clsx('stock-card-change', isPositive ? 'positive' : 'negative')}>
          {isPositive ? '+' : ''}{change.toFixed(2)} ({changePercent.toFixed(2)}%)
        </span>
      </div>

      {!compact && (volume || marketCap) && (
        <div className="stock-card-meta">
          {volume && (
            <span className="stock-card-meta-item">
              Vol: {(volume / 1000000).toFixed(1)}M
            </span>
          )}
          {marketCap && (
            <span className="stock-card-meta-item">
              MCap: {marketCap}
            </span>
          )}
        </div>
      )}
    </Card>
  )
}

// Action Card (for settings, features, etc.)
interface ActionCardProps {
  title: string
  description?: string
  icon?: ReactNode
  onClick?: () => void
  rightElement?: ReactNode
  disabled?: boolean
  className?: string
}

export function ActionCard({
  title,
  description,
  icon,
  onClick,
  rightElement,
  disabled = false,
  className
}: ActionCardProps) {
  return (
    <Card
      variant="default"
      padding="md"
      hover={!disabled && !!onClick}
      interactive={!disabled && !!onClick}
      onClick={disabled ? undefined : onClick}
      className={clsx('action-card', disabled && 'action-card--disabled', className)}
    >
      {icon && <div className="action-card-icon">{icon}</div>}
      <div className="action-card-content">
        <span className="action-card-title">{title}</span>
        {description && <span className="action-card-description">{description}</span>}
      </div>
      {rightElement || (onClick && <ChevronRight size={18} className="action-card-arrow" />)}
    </Card>
  )
}

export default Card
